import React, { useEffect, useCallback } from 'react'
import {
    useFloating,
    useInteractions,
    useDismiss,
    FloatingPortal,
} from '@floating-ui/react'
import styled, { css, keyframes } from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { ModalLogic, type ModalProps } from './logic'
import { Button } from '~common/components/button'

interface ModalOverlayStyleProps {
    isOpen?: boolean
    backdropBlur?: boolean
}

interface ModalDialogStyleProps {
    size?: 'extraSmall' | 'small' | 'medium' | 'large' | 'full'
    position?: 'center' | 'top' | 'bottom' | 'contain'
    scale?: number
    enableScroll?: boolean
}

export default function Modal(props: ModalProps) {
    const { logic, state } = useLogic(ModalLogic, props)

    const {
        isOpen,
        onClose,
        children,
        showCloseButton = true,
        closeOnOverlayClick = true,
        closeOnEscape = true,
        footer,
        size = 'medium',
        position = 'center',
        backdropBlur = true,
        ...restProps
    } = props

    const { refs, context } = useFloating({
        open: isOpen,
        onOpenChange: onClose,
    })

    const dismiss = useDismiss(context, {
        enabled: closeOnEscape,
        escapeKey: closeOnEscape,
    })

    const { getFloatingProps } = useInteractions([dismiss])

    const handleOverlayClick = useCallback(
        (e: React.MouseEvent) => {
            if (closeOnOverlayClick && e.target === e.currentTarget) {
                onClose()
            }
        },
        [closeOnOverlayClick, onClose],
    )

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
            return () => {
                document.body.style.overflow = 'unset'
            }
        }
    }, [isOpen])

    useEffect(() => {
        const calculateScale = () => {
            // Use Screen API for accurate available screen space
            // availWidth/availHeight account for OS UI elements like taskbars
            const screenWidth = window.screen.availWidth
            const screenHeight = window.screen.availHeight

            let scale = 1
            if (screenWidth < 800 || screenHeight < 900) {
                scale = 0.7
            }

            logic.setContainerScale(scale)
        }

        calculateScale()
        window.addEventListener('resize', calculateScale)

        return () => window.removeEventListener('resize', calculateScale)
    }, [])

    if (!isOpen) return null

    const modalContent = (
        <ModalOverlay
            isOpen={isOpen}
            backdropBlur={backdropBlur}
            ref={refs.setFloating}
            onClick={handleOverlayClick}
            {...getFloatingProps()}
            {...restProps}
        >
            <ModalDialog
                size={size}
                position={position}
                scale={state.containerScale}
            >
                {showCloseButton && (
                    <ModalHeader>
                        {showCloseButton && (
                            <Button
                                icon="close"
                                onClick={onClose}
                                variant="ghost"
                                size="large"
                            />
                        )}
                    </ModalHeader>
                )}
                <ModalBody
                    enableScroll={props.enableScroll}
                    scale={state.containerScale}
                    position={position}
                >
                    {children}
                </ModalBody>
            </ModalDialog>
        </ModalOverlay>
    )

    return <FloatingPortal>{modalContent}</FloatingPortal>
}

const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`

const fadeOut = keyframes`
    from {
        opacity: 1;
    }
    to {
        opacity: 0;
    }
`

const slideIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
`

const slideOut = keyframes`
    from {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
    to {
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
    }
`

const ModalOverlay = styled.div.withConfig({
    shouldForwardProp: (prop) => !['isOpen', 'backdropBlur'].includes(prop),
})<ModalOverlayStyleProps>`
    position: fixed;
    inset: 0;
    background-color: ${({ theme }) => theme.colors.greyScale[900]}40;
    backdrop-filter: ${({ backdropBlur }) =>
        backdropBlur ? 'blur(4px)' : 'none'};
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: ${({ theme }) => theme.spacing[4]};
    animation: ${({ isOpen }) => (isOpen ? fadeIn : fadeOut)}
        ${({ theme }) => theme.transitions.normal} ease-in-out;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
`

const getSizeStyles = (size: ModalDialogStyleProps['size'] = 'medium') => {
    switch (size) {
        case 'extraSmall':
            return css`
                max-width: 600px;
                width: 100%;
            `
        case 'small':
            return css`
                max-width: 700px;
                width: 100%;
            `
        case 'medium':
            return css`
                max-width: 800px;
                width: 100%;
            `
        case 'large':
            return css`
                max-width: 1000px;
                width: 100%;
                height: 100%;
                max-height: 800px;
            `
        case 'full':
            return css`
                max-width: 95vw;
                width: 95vw;
                height: 95vh;
            `
        default:
            return css``
    }
}

const getPositionStyles = (
    position: ModalDialogStyleProps['position'] = 'center',
) => {
    switch (position) {
        case 'center':
            return css`
                align-self: center;
                justify-content: flex-start;
            `
        case 'top':
            return css`
                align-self: flex-start;
                margin-top: ${({ theme }) => theme.spacing[8]};
            `
        case 'bottom':
            return css`
                align-self: flex-end;
                margin-bottom: ${({ theme }) => theme.spacing[8]};
            `
        case 'contain':
            return css`
                align-self: contain;
                align-items: center;
                justify-content: flex-start;
                padding: 0;
            `
        default:
            return css``
    }
}

const ModalDialog = styled.div.withConfig({
    shouldForwardProp: (prop) =>
        !['size', 'position', 'enableScroll'].includes(prop),
})<ModalDialogStyleProps>`
    background-color: ${({ theme }) => theme.colors.background.bg1};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    box-shadow: ${({ theme }) => theme.shadows['2xl']};
    border: 1px solid ${({ theme }) => theme.colors.border.primary};
    animation: ${({ theme }) => slideIn}
        ${({ theme }) => theme.transitions.normal} ease-out;
    max-height: 90vh;
    display: flex;
    flex-direction: column;

    ${({ size }) => getSizeStyles(size)}
    ${({ position }) => getPositionStyles(position)}
    position: relative;
`

const ModalHeader = styled.div`
    display: flex;
    position: absolute;
    right: ${({ theme }) => theme.spacing[4]};
    top: ${({ theme }) => theme.spacing[4]};
    z-index: 1000;
    align-items: center;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.spacing[4]};

    align-self: flex-end;
`

const ModalBody = styled.div<{
    position: ModalDialogStyleProps['position']
    scale?: number
    enableScroll?: boolean
}>`
    padding: ${({ theme }) => theme.spacing[6]};
    box-sizing: border-box;
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    top: 0;

    ${({ position }) =>
        position === 'contain' &&
        css`
            align-items: center;
            justify-content: flex-start;
            overflow: visible;
        `}

    ${({ enableScroll }) =>
        enableScroll &&
        css`
            overflow-y: auto;
            width: 100%;
            // hide scrollbar
            &::-webkit-scrollbar {
                display: none;
            }
            -ms-overflow-style: none;
            scrollbar-width: none;
        `}


    ${({ scale }) =>
        scale &&
        scale <= 0.7 &&
        css`
            scale: ${scale};
            justify-content: flex-start;
        `}
`
